import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSkin, type SkinId } from '@/store/SkinContext';
import { SkinMenu } from '@/components/SkinMenu';
import { UserMenu } from '@/components/UserMenu';
import { Icon } from '@/components/Icon';
import { EDICIONES } from '@/config/ediciones';

/** Pictograma de pista de mundoatletismo por skin (mismo criterio que la web
 *  de rankings: verde=lane, naranja=retro, rojo=editorial). */
export const SKIN_ICON: Record<SkinId, string> = {
  editorial: '/icon-pista-rojo-claro.svg',
  retro: '/icon-pista-naranja.svg',
  lane: '/icon-pista-verde.svg',
};

export const INSTAGRAM_URL = 'https://www.instagram.com/mund.oatletismo/';

/** En producción se ocultan entradas aún no listas (Ficha Atleta); en local
 *  (dev) siguen visibles para poder trabajarlas. */
const ES_DEV = import.meta.env.DEV;

export function AppShell({ children }: { children: ReactNode }) {
  const { skin } = useSkin();
  const { pathname } = useLocation();
  // En el Panel de equipo ocultamos el menú de navegación: el panel es el hub
  // y se navega a las herramientas pulsando las tarjetas. El panel vive en
  // /<id de edición> (p. ej. /malaga); /panel/* queda como compat.
  const seg = pathname.split('/')[1] ?? '';
  const enPanel = seg === 'panel' || EDICIONES.some((e) => e.id === seg);
  return (
    <div className="ld-shell">
      <header className="ld-header">
        <Link
          to="/panel"
          className="ld-header__brand"
          aria-label="Ir al panel"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {/* En el panel, la marca es la de la home pública (pista retro +
              Mundo Atletismo, decisión 2026-07-21); en el resto, Podios. */}
          <img className="ld-header__mark" src={enPanel ? SKIN_ICON.retro : SKIN_ICON[skin]} alt="" width={38} height={38} />
          <span>{enPanel ? 'Mundo Atletismo' : 'Podios Stories'}</span>
        </Link>
        {enPanel ? null : (
          <nav className="ld-header__nav">
            <NavLink to="/inicio" end className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Inicio
            </NavLink>
            <NavLink to="/panel" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Panel
            </NavLink>
            {/* Editor y Ficha Atleta (herramienta vieja de podios/vídeo) SOLO en
                dev: en prod no se navegan. Las rutas siguen existiendo por si hay
                que volver, pero sin enlace. render_atleta_video se ha sacado del
                deploy, así que su botón de vídeo quedaría inerte. */}
            {ES_DEV ? (
              <NavLink to="/editor" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                Editor
              </NavLink>
            ) : null}
            {ES_DEV ? (
              <NavLink to="/ficha-atleta" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                Ficha Atleta
              </NavLink>
            ) : null}
          </nav>
        )}
        <div className="ld-header__user">
          <SkinMenu />
          <a
            className="ld-icon-btn"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram @mund.oatletismo"
            title="Síguenos en Instagram"
          >
            <Icon name="instagram" size={20} />
          </a>
          <UserMenu />
        </div>
      </header>
      <main className="ld-main">{children}</main>
    </div>
  );
}
